import { defineStore } from 'pinia'
import type { RouteRecordRaw } from 'vue-router'
import asyncRoutes from '@/router/modules/asyncRoutes'
import constantRoutes from '@/router/modules/constantRoutes'
import { PERMISSION_STORE_ID } from './ids'

interface PermissionData {
  roles: string[]
  cff: string[] // custom feature flags
}

interface PermissionState {
  routes: RouteRecordRaw[]
  addRoutes: RouteRecordRaw[]
  roles: string[]
}

/**
 * Account tokens issued before the team → space rename (#162) still carry `team_admin`; a token
 * lives up to an hour. Until it is renewed, the old role counts as the new one, so space admins
 * keep their settings across the deploy.
 * TODO: remove in a later release; tokens from before the rename have expired by now.
 */
export function withRenamedRoles(roles: string[] | undefined | null): string[] {
  const list = Array.isArray(roles) ? [...roles] : []
  if (list.includes('team_admin') && !list.includes('space_admin')) {
    list.push('space_admin')
  }
  return list
}

/**
 * Check if user has permission for a route
 */
function hasPermission(data: PermissionData, route: RouteRecordRaw): boolean {
  let hasAppApproval = true

  if (route.meta?.cff) {
    const routeCff = route.meta.cff as string[]
    hasAppApproval = data.cff.some(app => routeCff.includes(app))
  }

  // `cffAll`: every listed flag must be enabled (in addition to the "any of" check of `cff`).
  if (route.meta?.cffAll) {
    const required = route.meta.cffAll as string[]
    hasAppApproval = hasAppApproval && required.every(flag => data.cff.includes(flag))
  }

  if (route.meta?.roles) {
    const routeRoles = route.meta.roles as string[]
    const hasRoleApproval = data.roles.some(role => routeRoles.includes(role))
    return hasAppApproval && hasRoleApproval
  }

  return hasAppApproval
}

/**
 * Filter routes recursively based on permissions
 */
export function filterAsyncRoutes(routes: RouteRecordRaw[], data: PermissionData): RouteRecordRaw[] {
  const res: RouteRecordRaw[] = []

  routes.forEach(route => {
    const tmp = { ...route }
    if (hasPermission(data, tmp)) {
      if (tmp.children) {
        tmp.children = filterAsyncRoutes(tmp.children, data)
      }
      res.push(tmp)
    }
  })

  return res
}

export const usePermissionStore = defineStore(PERMISSION_STORE_ID, {
  state: (): PermissionState => ({
    routes: [],
    addRoutes: [],
    roles: []
  }),

  actions: {
    resetState() {
      this.routes = []
      this.addRoutes = []
      this.roles = []
    },

    generateRoutes(data: PermissionData): RouteRecordRaw[] {
      // Temporary fix for users without feature flags
      if (!data.cff) {
        data.cff = []
      }

      data = { ...data, roles: withRenamedRoles(data.roles) }
      const accessedRoutes = filterAsyncRoutes(asyncRoutes, data)
      this.addRoutes = accessedRoutes
      this.routes = constantRoutes.concat(accessedRoutes)
      return accessedRoutes
    },

    setRoles(roles: string[]) {
      this.roles = withRenamedRoles(roles)
    }
  }
})
